import { useCallback, useEffect, useRef, useState, type PointerEvent, type MouseEvent } from 'react';

const LONG_MS = 500;
const MOVE_PX = 10;

export interface LongPressRowHandlers {
  onPointerDown: (e: PointerEvent<HTMLButtonElement>) => void;
  onPointerMove: (e: PointerEvent<HTMLButtonElement>) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onContextMenu: (e: MouseEvent<HTMLButtonElement>) => void;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}

export function useLongPressSelect() {
  const [mode, setMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const timerRef = useRef<number | null>(null);
  const startRef = useRef({ x: 0, y: 0 });
  const swallowedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const exit = useCallback(() => {
    clearTimer();
    setMode(false);
    setSelected(new Set());
  }, []);

  const enter = useCallback((id: string) => {
    setMode(true);
    setSelected(new Set([id]));
  }, []);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!mode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, exit]);

  const bindRow = useCallback(
    (id: string, enabled: boolean, onOpen?: () => void): LongPressRowHandlers => ({
      onPointerDown: (e: PointerEvent<HTMLButtonElement>) => {
        if (!enabled && !mode) return;
        startRef.current = { x: e.clientX, y: e.clientY };
        swallowedRef.current = false;
        clearTimer();
        if (!enabled) return;
        timerRef.current = window.setTimeout(() => {
          swallowedRef.current = true;
          enter(id);
        }, LONG_MS);
      },
      onPointerMove: (e: PointerEvent<HTMLButtonElement>) => {
        const dx = Math.abs(e.clientX - startRef.current.x);
        const dy = Math.abs(e.clientY - startRef.current.y);
        if (dx > MOVE_PX || dy > MOVE_PX) clearTimer();
      },
      onPointerUp: () => clearTimer(),
      onPointerCancel: () => clearTimer(),
      onContextMenu: (e: MouseEvent<HTMLButtonElement>) => {
        if (!enabled) return;
        e.preventDefault();
        swallowedRef.current = true;
        enter(id);
      },
      onClick: (e: MouseEvent<HTMLButtonElement>) => {
        if (swallowedRef.current) {
          e.preventDefault();
          e.stopPropagation();
          swallowedRef.current = false;
          return;
        }
        if (mode) {
          e.preventDefault();
          e.stopPropagation();
          if (enabled) toggle(id);
          return;
        }
        onOpen?.();
      },
    }),
    [enter, mode, toggle]
  );

  return { mode, selected, enter, toggle, exit, bindRow };
}
